import { option, string } from 'fp-ts';
import { useEffect, useMemo, useState } from 'react';
import { constVoid, pipe } from 'fp-ts/function';
import { findFirst, intersection } from 'fp-ts/Array';
import { useFormContext } from 'react-hook-form';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { DialogProps, Stack, SelectionCardGroup } from '@optimus-web/core';
import {
    PortfolioEnterpriseGroupResult,
    PortfolioEnterpriseGroupResultIEnumerablePagedResult,
} from '@mews-commander/data-access';
import { AsideDialog, UnsavedChangesDialog } from '@mews-commander/ui';
import { InjectedFormProps, withHookForm } from '@mews-commander/form';
import {
    ADD_TO_PROPERTY_GROUP_ENDING_POINT,
    CREATE_NEW_PROPERTY_GROUP_ENDING_POINT,
} from '../../utilities/userFlowTestIds';
import { FIELD_NAMES, FormValues } from './types/Form';
import { FORM_TYPE } from './constraints';
import { AddToExistingPropertyGroupFields, PropertyGroupDetailsFields } from './FormFields';

type PropertyGroupDialogProps = InjectedFormProps & {
    enterpriseIds: string[];
    onConfirm: () => Promise<void>;
    active: boolean;
    onCancel: () => void;
    isLoading: boolean;
    activeTabValue: FORM_TYPE;
    setActiveTabValue: (activeTab: FORM_TYPE) => void;
    propertyGroups?: PortfolioEnterpriseGroupResultIEnumerablePagedResult;
    selectedPropertyGroup?: PortfolioEnterpriseGroupResult;
};

const PropertyGroupDialog = ({
    enterpriseIds,
    active,
    onCancel,
    submit,
    isLoading,
    activeTabValue,
    setActiveTabValue,
    propertyGroups,
    selectedPropertyGroup,
}: PropertyGroupDialogProps) => {
    const [unsaved, setUnsaved] = useState(false);
    const { reset, formState, watch } = useFormContext();

    const selectedPropertyGroupId = watch(FIELD_NAMES.PROPERTY_GROUP_ID);

    const propertiesAlreadyInsideSelectedPropertyGroup = useMemo(
        () =>
            selectedPropertyGroupId != null && propertyGroups
                ? pipe(
                      propertyGroups.data,
                      findFirst((propertyGroup) => propertyGroup.id === selectedPropertyGroupId),
                      option.match(
                          () => [],
                          (propertyGroup) => pipe(propertyGroup.propertyIds, intersection(string.Eq)(enterpriseIds))
                      )
                  )
                : [],
        [selectedPropertyGroupId, propertyGroups, enterpriseIds]
    );

    useEffect(() => {
        if (!active) {
            reset();
        }
    }, [active, reset]);

    const handleClose = () => {
        if (formState.isDirty) {
            setUnsaved(true);
            return;
        }

        handleDiscard();
    };

    const handleDiscard = () => {
        setUnsaved(false);
        onCancel?.();
    };

    const isEditFormUnchanged = activeTabValue === FORM_TYPE.EDIT && !formState.isDirty;
    const isNewFormRequiredFieldsEmpty =
        activeTabValue === FORM_TYPE.CREATE_NEW && !formState.dirtyFields[FIELD_NAMES.NAME];

    const isPropertyGroupIdEmpty =
        activeTabValue === FORM_TYPE.ADD_TO_EXISTING && !formState.dirtyFields[FIELD_NAMES.PROPERTY_GROUP_ID];

    const isInsideSelectedPropertyGroup =
        activeTabValue === FORM_TYPE.ADD_TO_EXISTING &&
        enterpriseIds.length === propertiesAlreadyInsideSelectedPropertyGroup.length;

    const isSubmitButtonDisable =
        isLoading ||
        isEditFormUnchanged ||
        isNewFormRequiredFieldsEmpty ||
        isPropertyGroupIdEmpty ||
        isInsideSelectedPropertyGroup;

    const submitActionTextKey = [FORM_TYPE.CREATE_NEW, FORM_TYPE.EDIT].includes(activeTabValue)
        ? TextKey.Create
        : TextKey.Add;

    const createNewPropertyGroupTestId =
        activeTabValue === FORM_TYPE.CREATE_NEW ? CREATE_NEW_PROPERTY_GROUP_ENDING_POINT : '';

    const addToExistingPropertyGroupTestId =
        activeTabValue === FORM_TYPE.ADD_TO_EXISTING ? ADD_TO_PROPERTY_GROUP_ENDING_POINT : '';

    const actions = [
        {
            children: selectedPropertyGroup ? (
                <LocalizedText textKey={TextKey.Save} />
            ) : (
                <LocalizedText textKey={submitActionTextKey} />
            ),
            disabled: isSubmitButtonDisable,
            variant: 'primary',
            onClick: submit,
            'data-test-id': createNewPropertyGroupTestId || addToExistingPropertyGroupTestId,
            type: 'submit',
        },
    ] satisfies DialogProps['actions'];

    const noPropertyGroupsExist = propertyGroups?.totalRowCount === 0;

    const getTabContent = () => {
        switch (activeTabValue) {
            case FORM_TYPE.CREATE_NEW:
            case FORM_TYPE.EDIT:
                return <PropertyGroupDetailsFields formState={formState} enterpriseIds={enterpriseIds} />;
            case FORM_TYPE.ADD_TO_EXISTING:
                return (
                    <AddToExistingPropertyGroupFields
                        enterpriseIds={enterpriseIds}
                        noPropertyGroupsExist={noPropertyGroupsExist}
                        propertiesAlreadyInsideSelectedPropertyGroup={propertiesAlreadyInsideSelectedPropertyGroup}
                    />
                );
            default:
                return null;
        }
    };

    const asideDialogTitle = selectedPropertyGroup ? (
        <LocalizedText
            textKey={TextKey.EditPropertyGroup}
            substitutes={{ propertyGroupName: selectedPropertyGroup.name }}
        />
    ) : (
        <LocalizedText textKey={TextKey.PropertyAddGroupTitle} />
    );

    return (
        <>
            <AsideDialog
                title={asideDialogTitle}
                actions={actions}
                active={active}
                onClose={isLoading ? constVoid : handleClose}
                loading={isLoading}
            >
                <Stack vertical spacing="200">
                    {!selectedPropertyGroup && (
                        <SelectionCardGroup
                            templateColumns="repeat(2, 1fr)"
                            type="radio"
                            onChange={(_, tab) => setActiveTabValue(tab as FORM_TYPE)}
                            inputValue={activeTabValue}
                            options={[
                                {
                                    label: <LocalizedText textKey={TextKey.NewGroup} />,
                                    name: FORM_TYPE.CREATE_NEW,
                                    value: FORM_TYPE.CREATE_NEW,
                                },
                                {
                                    label: <LocalizedText textKey={TextKey.ExistingGroup} />,
                                    name: FORM_TYPE.ADD_TO_EXISTING,
                                    value: FORM_TYPE.ADD_TO_EXISTING,
                                },
                            ]}
                        />
                    )}
                    {getTabContent()}
                </Stack>
            </AsideDialog>
            {unsaved ? (
                <UnsavedChangesDialog onCancel={() => setUnsaved(false)} onDiscardChanges={handleDiscard} />
            ) : null}
        </>
    );
};

export const PropertyGroupDialogForm = withHookForm<FormValues, PropertyGroupDialogProps>()(PropertyGroupDialog);
